import React from 'react';
import { Menu, MenuGroup, MenuItem } from '@szhsin/react-menu';
import cx from 'classnames';
import ModalBoxes from 'common/feedback/ModalBoxes/ModalBoxes';
import Button from 'common/general/Button';
import Icon from 'common/general/Icon';
import { isSituationalOrUnexpectedEncounter } from 'constants/encounter';
import OutOfWindowIcon from 'images/icons/out-of-window.svg?react';
import { isEmpty } from 'lodash/lang';
import LightTooltip from 'mui/Tooltip/LightTooltip';
import { formatDate } from 'services/date';

import { usePatientInfo } from '../../../../../../PatientInfoContext';
import { useEncountersSection } from '../../../../../EncountersSectionContext';
import { getEventStartDate } from '../../../../utils';
import CancelAppointmentModal from '../../CancelAppointmentModal/CancelAppointmentModal';
import { useEncounterSegment } from '../../EncounterSegment';

import './ScheduledAppointments.scss';

export default function ScheduledAppointments() {
  const { currentStudy } = usePatientInfo();
  const { rescheduleAppointment, isAllowedToManageEncounters } = useEncountersSection();
  const { encounter, refetchTabData } = useEncounterSegment();
  const { encounterType, encounterEvents, removedFromProtocol, encounterFilterStatus, outOfWindow } = encounter;
  return (
    encounterEvents &&
    !isEmpty(encounterEvents) && (
      <div className="appointments-rows">
        <div className="appointments-rows-title">
          Scheduled Appointments
          {outOfWindow && (
            <LightTooltip
              arrow
              placement="top"
              title="Out-of-Window Encounter"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -7]
                      }
                    }
                  ]
                }
              }}
            >
              <span className="appointments-out-of-window">
                <OutOfWindowIcon className="appointments-out-of-window-icon" />
              </span>
            </LightTooltip>
          )}
        </div>
        {encounterEvents.map(function(encounterEvent) {
          const {
            id,
            start,
            description,
            cancelled,
            cancellationComments,
            windowStartDate,
            windowEndDate
          } = encounterEvent;
          return (
            <div key={id} className={cx('appointments-row', { cancelled })}>
              <LightTooltip
                arrow
                placement="top"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -7]
                        }
                      }
                    ]
                  }
                }}
                title={
                  <div className="date-window-tooltip">
                    <div className="date-window-tooltip-title">Expected Encounter Window:</div>
                    {windowStartDate && windowEndDate && (
                      <div className="date-window-tooltip-range">
                        {formatDate(windowStartDate)}
                        {' - '}
                        {formatDate(windowEndDate)}
                      </div>
                    )}
                  </div>
                }
              >
                <div className="event-start-date">{getEventStartDate(start, currentStudy)}</div>
              </LightTooltip>
              <div className="appointment-comments">
                {getCommentMessage(cancelled, cancellationComments, description, removedFromProtocol)}
              </div>
              <div className="control-buttons">
                <Menu
                  align="end"
                  menuButton={
                    <Button disabled={cancelled || !isAllowedToManageEncounters} size="h40" priority="low">
                      <Icon>more_vert</Icon>
                    </Button>
                  }
                >
                  <MenuGroup>
                    {(encounterFilterStatus === 'Scheduled' || isSituationalOrUnexpectedEncounter(encounterType)) && (
                      <MenuItem onClick={() => rescheduleAppointment(encounterEvent)}>Reschedule Appointment</MenuItem>
                    )}
                    <MenuItem onClick={() => cancelAppointment(encounterEvent)}>Cancel Appointment</MenuItem>
                  </MenuGroup>
                </Menu>
              </div>
            </div>
          );
        })}
      </div>
    )
  );

  function cancelAppointment(event) {
    ModalBoxes.open({
      component: <CancelAppointmentModal event={event} onSuccess={refetchTabData} />
    });
  }
}

function getCommentMessage(cancelled, cancellationComments, description, removedFromProtocol) {
  if (cancelled && !isEmpty(cancellationComments)) {
    return cancellationComments;
  }
  if (removedFromProtocol) {
    return 'Encounter removed from protocol.  Please reschedule with valid encounter';
  }
  return description;
}
